import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  Save, 
  ArrowLeft, 
  Upload, 
  Info, 
  Layers, 
  Zap, 
  Clock, 
  Trash2, 
  Plus, 
  Loader2,
  DollarSign,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import api from '@/lib/api';
import { 
  VisibilidadeProduto, 
  TipoManufatura, 
  Filamento, 
  Maquina, 
  CustoCalculado,
  Produto
} from '@/types';

export const EditarProduto = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('geral');
  const [isUploading, setIsUploading] = useState(false);
  const [isCalculando, setIsCalculando] = useState(false);
  const [resultadoCusto, setResultadoCusto] = useState<CustoCalculado | null>(null);

  const [formData, setFormData] = useState({
    nome: '',
    skuInterno: '',
    descricao: '',
    categoriaId: '',
    ativo: true,
    destaque: false,
    modelo3D: '',
    arquivo3DUrl: '',
    nomeArquivo3D: '',
    fotos: [] as string[],
    visibilidade: VisibilidadeProduto.CATALOGO,
    exclusivoSalaSecreta: false,
    
    // Ficha Técnica
    filamentos: [] as { filamentoId: string; gramas: number; tipo: string }[],
    tempoImpressaoMinutos: '0',
    tempoPreparacaoMinutos: '5',
    tempoManufaturaMinutos: '0',
    maquinaPadraoId: '',
    quantidadeLote: '1',
    embalagens: [] as { embalagemId: string; quantidade: number }[],
    tipoManufatura: [] as TipoManufatura[],
    videoTimelapseUrl: '',
    
    // Taxas e Precificação
    taxaRefugo: '10',
    taxaDesperdicio: '5',
    markup: '200',
    taxaImpostos: '15',
    precoVenda: '0',
    estoqueDisponivel: '0',
    estoqueMinimo: '5',
  });

  // Queries
  const { data: produto, isLoading: isLoadingProduto } = useQuery<Produto>({ 
    queryKey: ['produto', id], 
    queryFn: () => api.get(`/produtos/${id}`).then(res => res.data.data) 
  });
  
  const { data: categorias } = useQuery({ queryKey: ['categorias'], queryFn: () => api.get('/categorias').then(res => res.data.data) });
  const { data: filamentos } = useQuery<Filamento[]>({ queryKey: ['filamentos'], queryFn: () => api.get('/insumos/filamentos').then(res => res.data.data) });
  const { data: maquinas } = useQuery<Maquina[]>({ queryKey: ['maquinas'], queryFn: () => api.get('/maquinas').then(res => res.data.data) });
  const { data: embalagens } = useQuery({ queryKey: ['embalagens'], queryFn: () => api.get('/insumos/embalagens').then(res => res.data.data) });

  // Popula form ao carregar
  useEffect(() => {
    if (produto) {
      // Mapeia filamentos da relação
      const filamentosMapeados = produto.configuracaoFilamentos?.map(f => ({
        filamentoId: f.filamentoId,
        gramas: f.gramas || 0,
        tipo: f.tipo
      })) || (produto.filamentoId ? [{
        filamentoId: produto.filamentoId,
        gramas: Number(produto.gramasFilamentoProduto || 0) + Number(produto.gramasFilamentoSuporte || 0),
        tipo: 'PECA'
      }] : []);

      // Parser de fotos
      let fotosArray: string[] = [];
      if (produto.fotos) {
        try {
          fotosArray = typeof produto.fotos === 'string' ? JSON.parse(produto.fotos) : produto.fotos;
        } catch (e) {
          fotosArray = [];
        }
      }

      setFormData({
        nome: produto.nome || '',
        skuInterno: produto.skuInterno || '',
        descricao: produto.descricao || '',
        categoriaId: produto.categoriaId || '',
        ativo: produto.ativo,
        destaque: produto.destaque,
        modelo3D: produto.modelo3D || '',
        arquivo3DUrl: produto.arquivo3DUrl || '',
        nomeArquivo3D: produto.nomeArquivo3D || '',
        fotos: fotosArray,
        visibilidade: produto.visibilidade || VisibilidadeProduto.CATALOGO,
        exclusivoSalaSecreta: produto.exclusivoSalaSecreta || false,
        
        filamentos: filamentosMapeados,
        tempoImpressaoMinutos: (produto.tempoImpressaoMinutos || 0).toString(),
        tempoPreparacaoMinutos: (produto.tempoPreparacaoMinutos || 5).toString(),
        tempoManufaturaMinutos: (produto.tempoManufaturaMinutos || 0).toString(),
        maquinaPadraoId: produto.maquinaPadraoId || '',
        embalagens: typeof produto.embalagens === 'string' ? JSON.parse(produto.embalagens) : (produto.embalagens || []),
        tipoManufatura: typeof produto.tipoManufatura === 'string' ? JSON.parse(produto.tipoManufatura) : (produto.tipoManufatura || []),
        videoTimelapseUrl: produto.videoTimelapseUrl || '',
        
        taxaRefugo: (produto.taxaRefugo || 10).toString(),
        taxaDesperdicio: (produto.taxaDesperdicio || 5).toString(),
        markup: (produto.markup || 200).toString(),
        taxaImpostos: (produto.taxaImpostos || 15).toString(),
        precoVenda: (produto.precoVenda || 0).toString(),
        estoqueDisponivel: (produto.estoqueDisponivel || 0).toString(),
        estoqueMinimo: (produto.estoqueMinimo || 5).toString(),
        quantidadeLote: (produto.quantidadeLote || 1).toString(),
      });
    }
  }, [produto]);

  const handleCalcular = async () => {
    if (formData.filamentos.length === 0) return;

    setIsCalculando(true);
    try {
      const payload = {
        filamentos: formData.filamentos.filter(f => f.filamentoId).map(f => ({ ...f, gramas: Number(f.gramas) })),
        tempoImpressaoMinutos: Number(formData.tempoImpressaoMinutos) || 0,
        tempoPreparacaoMinutos: Number(formData.tempoPreparacaoMinutos) || 0,
        tempoManufaturaMinutos: Number(formData.tempoManufaturaMinutos) || 0,
        maquinaId: formData.maquinaPadraoId || undefined,
        embalagens: formData.embalagens.filter(e => e.embalagemId).map(e => ({ ...e, quantidade: Number(e.quantidade) })),
        markup: Number(formData.markup) || 0,
        taxaImpostos: Number(formData.taxaImpostos) || 0,
        taxaRefugo: Number(formData.taxaRefugo) || 0,
        taxaDesperdicio: Number(formData.taxaDesperdicio) || 0,
        precoVenda: Number(formData.precoVenda) || undefined,
        quantidadeLote: Number(formData.quantidadeLote) || 1
      };

      const response = await api.post('/produtos/calculo/preview', payload);
      setResultadoCusto(response.data.data.calculo);
      
      if (!formData.precoVenda || formData.precoVenda === '0') {
         setFormData(prev => ({ ...prev, precoVenda: response.data.data.calculo.precificacao.precoVendaSugerido.toString() }));
      }
    } catch (error) {
       console.error('Erro ao calcular custo:', error);
    } finally {
       setIsCalculando(false);
    }
  };

  // Debounce para cálculo automático
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.filamentos.length > 0) {
        handleCalcular();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    formData.filamentos,
    formData.tempoImpressaoMinutos,
    formData.tempoPreparacaoMinutos,
    formData.tempoManufaturaMinutos,
    formData.maquinaPadraoId,
    formData.markup,
    formData.taxaImpostos,
    formData.taxaRefugo,
    formData.taxaDesperdicio,
    formData.embalagens
  ]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addFilamento = () => {
    setFormData(prev => ({
      ...prev,
      filamentos: [...prev.filamentos, { filamentoId: '', gramas: 0, tipo: 'PECA' }]
    }));
  };

  const removeFilamento = (index: number) => {
    setFormData(prev => ({
      ...prev,
      filamentos: prev.filamentos.filter((_, i) => i !== index)
    }));
  };

  const updateFilamento = (index: number, data: any) => {
    setFormData(prev => ({
      ...prev,
      filamentos: prev.filamentos.map((f, i) => i === index ? { ...f, ...data } : f)
    }));
  };

  const addEmbalagem = () => {
    setFormData(prev => ({
      ...prev,
      embalagens: [...prev.embalagens, { embalagemId: '', quantidade: 1 }]
    }));
  };

  const removeEmbalagem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      embalagens: prev.embalagens.filter((_, i) => i !== index)
    }));
  };

  const updateEmbalagem = (index: number, data: any) => {
    setFormData(prev => ({
      ...prev,
      embalagens: prev.embalagens.map((e, i) => i === index ? { ...e, ...data } : e)
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadFormData = new FormData();
      Array.from(files).forEach(file => uploadFormData.append('files', file));

      const response = await api.post('/upload/multiple', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const urls = response.data.data.map((f: any) => f.url);
      setFormData(prev => ({ ...prev, fotos: [...prev.fotos, ...urls] }));
    } catch (error) {
       console.error('Erro no upload de fotos:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      fotos: prev.fotos.filter((_, i) => i !== index)
    }));
  };

  const atualizarProduto = useMutation({
    mutationFn: (data: any) => api.put(`/produtos/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      queryClient.invalidateQueries({ queryKey: ['produto', id] });
      navigate('/admin/produtos');
    },
    onError: (err: any) => {
      alert('Erro ao atualizar produto: ' + (err.response?.data?.message || err.message));
    }
  });

  const handleSubmit = () => {
    if (!formData.nome || !formData.skuInterno || !formData.categoriaId) {
      alert('Preencha os campos obrigatórios');
      return;
    }

    const payload = {
      ...formData,
      maquinaPadraoId: formData.maquinaPadraoId || undefined,
      filamentos: formData.filamentos
        .filter(f => f.filamentoId && Number(f.gramas) >= 0)
        .map(f => ({ ...f, gramas: Number(f.gramas) })),
      embalagens: formData.embalagens
        .filter(e => e.embalagemId && Number(e.quantidade) > 0)
        .map(e => ({ ...e, quantidade: Number(e.quantidade) })),
      tempoImpressaoMinutos: Number(formData.tempoImpressaoMinutos) || 0,
      tempoPreparacaoMinutos: Number(formData.tempoPreparacaoMinutos) || 0,
      tempoManufaturaMinutos: Number(formData.tempoManufaturaMinutos) || 0,
      taxaRefugo: Number(formData.taxaRefugo) || 0,
      taxaDesperdicio: Number(formData.taxaDesperdicio) || 0,
      markup: Number(formData.markup) || 0,
      taxaImpostos: Number(formData.taxaImpostos) || 0,
      precoVenda: Number(formData.precoVenda) || 0,
      estoqueDisponivel: Number(formData.estoqueDisponivel) || 0,
      estoqueMinimo: Number(formData.estoqueMinimo) || 0,
      quantidadeLote: Number(formData.quantidadeLote) || 1,
      videoTimelapseUrl: formData.videoTimelapseUrl?.trim() ? (
        formData.videoTimelapseUrl.startsWith('http') ? formData.videoTimelapseUrl : `https://${formData.videoTimelapseUrl}`
      ) : ''
    };

    atualizarProduto.mutate(payload);
  };

  if (isLoadingProduto) return <div className="flex justify-center p-20"><Loader2 className="h-8 w-8 animate-spin text-fort3d-laranja" /></div>;

  return (
    <div className="w-full py-6 px-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/produtos')} className="rounded-full">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Editar <span className="text-fort3d-laranja">Produto</span></h1>
            <p className="text-sm text-gray-500 font-medium">Atualize as informações, fotos e ficha técnica</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/admin/produtos')}>Cancelar</Button>
          <Button 
            className="bg-fort3d-laranja hover:bg-fort3d-laranja/90 text-white font-bold px-8 shadow-lg shadow-fort3d-laranja/20" 
            onClick={handleSubmit} 
            disabled={atualizarProduto.isPending}
          >
            {atualizarProduto.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar Alterações
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100 p-1 rounded-xl w-full justify-start mb-6">
              <TabsTrigger value="geral" className="rounded-lg">
                <Info className="h-4 w-4 mr-2" /> Informações
              </TabsTrigger>
              <TabsTrigger value="tecnica" className="rounded-lg">
                <Layers className="h-4 w-4 mr-2" /> Ficha Técnica
              </TabsTrigger>
              <TabsTrigger value="preco" className="rounded-lg">
                <DollarSign className="h-4 w-4 mr-2" /> Preço e Estoque
              </TabsTrigger>
            </TabsList>

            <TabsContent value="geral" className="space-y-6">
              <Card className="border-none shadow-sm overflow-hidden">
                <CardHeader className="bg-gray-50/50">
                  <CardTitle className="text-lg">Dados Principais</CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input label="Nome do Produto" value={formData.nome} onChange={(e) => handleChange('nome', e.target.value)} required />
                    <Input label="SKU Interno" value={formData.skuInterno} onChange={(e) => handleChange('skuInterno', e.target.value)} required />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                       <label className="text-sm font-bold text-gray-700">Categoria</label>
                       <Select value={formData.categoriaId} onValueChange={(v) => handleChange('categoriaId', v)}>
                         <SelectTrigger className="bg-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                         <SelectContent>
                           {categorias?.map((cat: any) => (
                             <SelectItem key={cat.id} value={cat.id}>{cat.nome}</SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-sm font-bold text-gray-700">Visibilidade</label>
                       <Select value={formData.visibilidade} onValueChange={(v) => handleChange('visibilidade', v)}>
                         <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                         <SelectContent>
                            <SelectItem value={VisibilidadeProduto.CATALOGO}>Público</SelectItem>
                            <SelectItem value={VisibilidadeProduto.SALA_SECRETA}>Sala Secreta</SelectItem>
                            <SelectItem value={VisibilidadeProduto.AMBOS}>Ambos</SelectItem>
                         </SelectContent>
                       </Select>
                    </div>
                  </div>

              <Input
                label="Descrição Curta"
                value={formData.descricao}
                onChange={(e) => handleChange('descricao', e.target.value)}
                placeholder="Uma breve descrição do produto..."
              />

              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Upload className="h-4 w-4 text-fort3d-laranja" />
                  Link Vídeo Timelapse (YouTube)
                </label>
                <Input
                  value={formData.videoTimelapseUrl}
                  onChange={(e) => handleChange('videoTimelapseUrl', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-gray-50 border-gray-200 focus:ring-fort3d-laranja"
                />
                {formData.videoTimelapseUrl && formData.videoTimelapseUrl.includes('youtube.com') && (
                  <div className="aspect-video rounded-xl overflow-hidden bg-black mt-2">
                    <iframe 
                      width="100%" 
                      height="100%" 
                      src={`https://www.youtube.com/embed/${formData.videoTimelapseUrl.split('v=')[1]?.split('&')[0]}`}
                      title="YouTube video player" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                )}
              </div>
                  <div className="h-px bg-gray-200 w-full" />
                  <div className="space-y-4">
                    <label className="text-sm font-bold text-gray-700">Fotos do Produto</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
                      {formData.fotos.map((foto, index) => (
                        <div key={index} className="relative aspect-square rounded-xl border border-gray-200 overflow-hidden group shadow-sm">
                          <img src={foto} className="w-full h-full object-cover" alt="" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="icon" onClick={() => removeFoto(index)} className="h-8 w-8 rounded-full">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:bg-fort3d-laranja/5 cursor-pointer">
                        {isUploading ? <Loader2 className="h-6 w-6 text-fort3d-laranja animate-spin" /> : <Upload className="h-6 w-6 text-gray-400" />}
                        <span className="text-[10px] text-gray-500 font-bold mt-2">Add Foto</span>
                        <input type="file" className="hidden" multiple accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tecnica" className="space-y-6">
               <Card className="border-none shadow-sm">
                <CardContent className="p-6 space-y-8">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                         <Layers className="h-5 w-5 text-gray-400" />
                         <span className="font-bold text-gray-800">Materiais</span>
                      </div>
                      <Button onClick={addFilamento} variant="outline" size="sm"><Plus className="h-4 w-4 mr-1" /> Add Material</Button>
                    </div>
                    <div className="space-y-3">
                      {formData.filamentos.map((item, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-gray-50/50 rounded-xl items-end border border-gray-100">
                          <div className="md:col-span-5 space-y-2">
                             <label className="text-[10px] font-black text-gray-400">Filamento</label>
                             <Select value={item.filamentoId} onValueChange={(v) => updateFilamento(index, { filamentoId: v })}>
                                <SelectTrigger className="bg-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                                <SelectContent>{filamentos?.map(f => <SelectItem key={f.id} value={f.id}>{f.fabricante} {f.cor}</SelectItem>)}</SelectContent>
                             </Select>
                          </div>
                          <div className="md:col-span-3 space-y-2">
                             <label className="text-[10px] font-black text-gray-400">Gramas (g)</label>
                             <Input type="number" className="bg-white" value={item.gramas} onChange={(e) => updateFilamento(index, { gramas: Number(e.target.value) })} />
                          </div>
                          <div className="md:col-span-3 space-y-2">
                             <label className="text-[10px] font-black text-gray-400">Finalidade</label>
                             <Select value={item.tipo} onValueChange={(v) => updateFilamento(index, { tipo: v })}>
                                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                   <SelectItem value="PECA">Peça</SelectItem>
                                   <SelectItem value="SUPORTE">Suporte</SelectItem>
                                   <SelectItem value="DETALHE">Detalhe</SelectItem>
                                </SelectContent>
                             </Select>
                          </div>
                          <div className="md:col-span-1 pb-1">
                            <Button variant="ghost" size="icon" onClick={() => removeFilamento(index)} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div className="space-y-4">
                        <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-gray-400" /><span className="font-bold text-gray-800">Tempos</span></div>
                        <div className="grid grid-cols-2 gap-4">
                           <Input label="Impressão (min)" type="number" value={formData.tempoImpressaoMinutos} onChange={(e) => handleChange('tempoImpressaoMinutos', e.target.value)} />
                           <Input label="Mão Obra (min)" type="number" value={formData.tempoPreparacaoMinutos} onChange={(e) => handleChange('tempoPreparacaoMinutos', e.target.value)} />
                        </div>
                     </div>
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 font-bold text-gray-800"><Zap className="h-5 w-5 text-gray-400" /> Máquina e Lote</div>
                        <div className="grid grid-cols-2 gap-4">
                           <Select value={formData.maquinaPadraoId} onValueChange={(v) => handleChange('maquinaPadraoId', v)}>
                             <SelectTrigger className="bg-gray-50/50"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                             <SelectContent>{maquinas?.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}</SelectContent>
                           </Select>
                           <div className="space-y-1">
                              <Input 
                                type="number" 
                                placeholder="Qtd no Lote" 
                                className="bg-gray-50/50 h-10" 
                                value={formData.quantidadeLote} 
                                onChange={(e) => handleChange('quantidadeLote', e.target.value)} 
                              />
                              <p className="text-[9px] text-gray-400 font-medium">Divisor de custo</p>
                           </div>
                        </div>
                      </div>
                   </div>

                   <div className="space-y-4 pt-6 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-bold text-gray-800"><Package className="h-5 w-5 text-gray-400" /> Insumos Adicionais e Embalagens</div>
                      <Button onClick={addEmbalagem} variant="outline" size="sm" className="h-8 border-dashed border-gray-300 hover:border-fort3d-laranja hover:text-fort3d-laranja">
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar Insumo
                      </Button>
                    </div>
                    
                    {formData.embalagens.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                        <p className="text-xs text-gray-400 font-medium">Nenhum insumo adicional selecionado (opcional)</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formData.embalagens.map((item, index) => (
                          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-white border border-gray-200 rounded-xl items-end relative hover:border-fort3d-laranja/30 animate-in fade-in slide-in-from-top-1 duration-200">
                            <div className="md:col-span-8 flex flex-col gap-1.5">
                              <label className="text-[10px] uppercase font-black text-gray-400">Insumo/Embalagem</label>
                              <Select 
                                value={item.embalagemId} 
                                onValueChange={(val) => updateEmbalagem(index, { embalagemId: val })}
                              >
                                <SelectTrigger className="bg-gray-50/50">
                                  <SelectValue placeholder="Selecione o Insumo" />
                                </SelectTrigger>
                                <SelectContent>
                                  {embalagens?.map((emb: any) => (
                                    <SelectItem key={emb.id} value={emb.id}>
                                      {emb.nome} (R$ {Number(emb.custoUnitario).toFixed(2)})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="md:col-span-3 flex flex-col gap-1.5">
                              <label className="text-[10px] uppercase font-black text-gray-400">Quantidade</label>
                              <Input
                                type="number"
                                value={item.quantidade}
                                onChange={(e) => updateEmbalagem(index, { quantidade: Number(e.target.value) })}
                                className="bg-gray-50/50"
                              />
                            </div>
                            <div className="md:col-span-1 pb-1 flex justify-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeEmbalagem(index)}
                                className="h-10 w-10 text-gray-400 hover:text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="preco" className="space-y-6">
                <Card className="border-none shadow-sm">
                  <CardContent className="p-6 space-y-6">
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Input label="Markup (%)" type="number" value={formData.markup} onChange={(e) => handleChange('markup', e.target.value)} />
                        <Input label="Preço de Venda Praticado (R$)" type="number" value={formData.precoVenda} onChange={(e) => handleChange('precoVenda', e.target.value)} />
                        <Input label="Estoque" type="number" value={formData.estoqueDisponivel} onChange={(e) => handleChange('estoqueDisponivel', e.target.value)} />
                     </div>
                  </CardContent>
               </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-1 text-white">
          <div className="sticky top-24 space-y-4">
            <Card className="border-none shadow-xl bg-gray-900 border-l-4 border-fort3d-laranja">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-gray-400">Custo Total Real</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                 <div className="text-4xl font-black text-fort3d-laranja">R$ {resultadoCusto?.custoRealTotal.toFixed(2) || '0,00'}</div>
                 <div className="h-px bg-white/10 w-full" />
                 <div className="space-y-3 text-[10px] text-gray-300">
                    <div className="flex justify-between"><span>Materiais:</span><span className="font-bold">R$ {((resultadoCusto?.composicao.filamentoProduto || 0) + (resultadoCusto?.composicao.filamentoSuporte || 0)).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Energia/Deprec:</span><span className="font-bold">R$ {((resultadoCusto?.composicao.energia || 0) + (resultadoCusto?.composicao.depreciacao || 0)).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Trabalho:</span><span className="font-bold">R$ {((resultadoCusto?.composicao.preparacao || 0) + (resultadoCusto?.composicao.manufatura || 0)).toFixed(2)}</span></div>
                 </div>

                 <div className="h-px bg-white/10 w-full" />
                 <div className="flex justify-between items-center py-2 px-1">
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Sugerido</span>
                    <span className="text-2xl font-black text-fort3d-laranja">R$ {resultadoCusto?.precificacao.precoVendaSugerido.toFixed(2) || '0,00'}</span>
                 </div>

                 <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="text-xs text-green-400 uppercase font-bold mb-1">LUCRO COM PREÇO ATUAL</div>
                    <div className="text-2xl font-black text-green-400">R$ {resultadoCusto?.precificacao.lucroPorPeca.toFixed(2) || '0,00'}</div>
                    <div className="text-[10px] text-gray-400 mt-1">{resultadoCusto?.precificacao.margemLucroPercent.toFixed(1)}% de margem real</div>
                 </div>
              </CardContent>
              {isCalculando && <div className="p-3 text-center text-[10px] text-gray-400 animate-pulse bg-white/5">ATUALIZANDO...</div>}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
